import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { currentMembership, useHousehold, type Membership } from '@/lib/queries';
import { ageBandLabel, date, dateTime, gradeLabel, money, unpaidText } from '@/lib/format';
import { memberLinks } from '@/lib/links';
import { ErrorNote, Spinner } from '@/components/Layout';
import { PhotoUpload, Avatar } from '@/components/Photo';
import { Seal } from '@/components/Brand';
import { MemberCodeButtons } from '@/components/MemberCode';
import { ledgerHow, type LedgerRow } from '@/pages/admin/AdminPayments';

interface PersonDetail {
  id: string; household_id: string; first_name: string; last_name: string; email: string | null; phone: string | null;
  date_of_birth: string | null; grade_rank: number | null; graded_on: string | null; is_household_manager: boolean;
  emergency_contact_name: string | null; emergency_contact_phone: string | null; medical_notes: string | null;
  photo_path: string | null; photo_consent: boolean; member_number: number | null; member_code: string; started_goju_ryu_on: string | null;
  frozen_at: string | null; frozen_reason: string | null;
  dojo: { id: string; name: string; short_name: string | null } | null;
  memberships: (Membership & { price_pence: number })[];
  gradings: { id: string; graded_on: string; for_rank: number; result: string; recommended: boolean | null; recommended_by: string | null; event: { title: string; slug: string } | null }[];
  bookings: { id: string; status: string; price_pence: number; pay_on_day: boolean; desk_note: string | null; checked_in_at: string | null; option: { name: string } | null; event: { title: string; slug: string; starts_at: string } | null }[];
}

const RESULT: Record<string, string> = { passed: 'Passed', failed: 'Not this time', registered: 'Entered', withdrawn: 'Withdrawn' };

/**
 * One person, for the people who may see them without being an admin: their own
 * household, or an instructor's students. The database decides who that is; a
 * person outside both simply isn't found. Payments show only to the household.
 */
export function PersonPage() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const household = useHousehold();
  const person = useQuery({
    queryKey: ['person-page', id],
    queryFn: async () => {
      const { data, error } = await supabase.from('person')
        .select('id,household_id,first_name,last_name,email,phone,date_of_birth,grade_rank,graded_on,is_household_manager,emergency_contact_name,emergency_contact_phone,medical_notes,photo_path,photo_consent,member_number,member_code,started_goju_ryu_on,frozen_at,frozen_reason,dojo:home_dojo_id(id,name,short_name),memberships:membership(id,starts_on,ends_on,status,price_pence,plan:plan_id(name)),gradings:grading!grading_person_id_fkey(id,graded_on,for_rank,result,recommended,recommended_by,event:event_id(title,slug)),bookings:event_registration(id,status,price_pence,pay_on_day,desk_note,checked_in_at,option:option_id(name),event:event_id(title,slug,starts_at))')
        .eq('id', id).maybeSingle();
      if (error) throw error;
      return data as unknown as PersonDetail | null;
    },
  });
  const own = !!person.data && person.data.household_id === household.data?.householdId;
  const payments = useQuery({
    queryKey: ['person-page-payments', id],
    enabled: own,
    queryFn: async () => {
      const { data, error } = await supabase.from('ledger').select('id,kind,on_date,amount_pence,method,payment_method_id,collected_by,status,what_for,what_for_detail,people')
        .eq('household_id', person.data!.household_id).order('created_at', { ascending: false });
      if (error) throw error;
      return (data as LedgerRow[]).filter(r => r.people.some(p => p.id === id));
    },
  });

  if (person.isLoading || household.isLoading) return <Spinner />;
  if (person.error) return <ErrorNote>We couldn't load this page. Please refresh.</ErrorNote>;
  const p = person.data;
  if (!p) return <ErrorNote>This person isn't in your household or among your students.</ErrorNote>;

  const name = `${p.first_name} ${p.last_name}`;
  const m = currentMembership(p as never);
  const today = new Date().toISOString().slice(0, 10);
  const current = !!m && m.status === 'active' && m.ends_on >= today;
  const memberships = [...p.memberships].sort((a, b) => b.starts_on.localeCompare(a.starts_on));
  const gradings = p.gradings.filter(g => g.result !== 'withdrawn').sort((a, b) => b.graded_on.localeCompare(a.graded_on));
  const bookings = p.bookings.filter(b => b.status !== 'cancelled' && b.event).sort((a, b) => b.event!.starts_at.localeCompare(a.event!.starts_at));
  const row = (label: string, value: React.ReactNode) => value ? <div className="flex gap-4 border-b border-stone-100 py-2 last:border-0"><dt className="w-40 shrink-0 text-ink-500">{label}</dt><dd className="min-w-0">{value}</dd></div> : null;

  return (
    <div className="space-y-6">
      <Link to={own ? '/home' : '/dojo'} className="text-sm text-ink-500 hover:underline">← {own ? 'My household' : 'My dojo'}</Link>

      <section className="card flex items-start gap-4">
        {own ? <PhotoUpload personId={p.id} path={p.photo_path} name={name} size={72} onChanged={() => qc.invalidateQueries({ queryKey: ['person-page', id] })} />
          : <Avatar path={p.photo_path} name={name} size={72} />}
        <div className="min-w-0 flex-1">
          <h1 className="text-3xl">{name}</h1>
          <p className="text-ink-500">
            {gradeLabel(p.grade_rank)}
            {p.dojo && <> · <Link to={memberLinks.dojo(p.dojo.id)} className="link">{p.dojo.short_name ?? p.dojo.name}</Link></>}
            {ageBandLabel(p.date_of_birth) && <> · {ageBandLabel(p.date_of_birth)}</>}
          </p>
          <p className="mt-1 text-[15px]">
            {p.frozen_at ? <span className="font-semibold text-indigo-600">Membership paused</span>
              : current ? <>Member until <strong>{date(m!.ends_on)}</strong></>
              : m ? <span className="font-semibold text-brand-700">Membership ended {date(m.ends_on)}</span>
              : <span className="font-semibold text-brand-700">Not a member yet</span>}
            {p.member_number && <span className="text-ink-500"> · EGKA member no. {p.member_number}</span>}
            {p.is_household_manager && <span className="text-ink-500"> · Account holder</span>}
          </p>
          {p.frozen_at && p.frozen_reason && <p className="mt-1 text-[15px]">{p.frozen_reason}</p>}
          {own && <div className="mt-3"><MemberCodeButtons person={p} /></div>}
        </div>
        {current && !p.frozen_at && <Seal size={46} />}
      </section>

      <section className="card">
        <h2 className="mb-2 text-xl">Details</h2>
        <dl className="text-[15px]">
          {row('Date of birth', date(p.date_of_birth))}
          {row('Email', p.email)}
          {row('Phone', p.phone)}
          {row('Emergency contact', [p.emergency_contact_name, p.emergency_contact_phone].filter(Boolean).join(' · '))}
          {row('Medical notes', p.medical_notes)}
          {row('Started Goju-Ryu', date(p.started_goju_ryu_on))}
          {row('Graded', date(p.graded_on))}
          {ageBandLabel(p.date_of_birth) !== 'Adult' && row('Event photos', p.photo_consent ? 'OK to use on the website and social media' : 'Not to be used')}
        </dl>
        {own && <p className="mt-3 text-sm text-ink-500">To change any of these, email the Membership Secretary at membership.secretary@egka.org.uk.</p>}
      </section>

      <section>
        <h2 className="text-2xl">Memberships</h2>
        {!memberships.length ? <p className="card mt-3 text-[15px] text-ink-500">No memberships yet.</p> : (
          <ul className="card mt-3 divide-y divide-stone-100 p-0">
            {memberships.map(x => (
              <li key={x.id} className="flex flex-wrap justify-between gap-2 px-5 py-3 text-[15px]">
                <span>{x.plan?.name ?? 'Membership'}</span>
                <span className="text-ink-500">{date(x.starts_on)} to {date(x.ends_on)} · {x.status === 'active' ? (x.ends_on >= today ? 'Current' : 'Ended') : x.status === 'pending' ? 'Being paid' : x.status === 'cancelled' ? 'Cancelled' : 'Ended'}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h2 className="text-2xl">Gradings</h2>
        {!gradings.length ? <p className="card mt-3 text-[15px] text-ink-500">No gradings recorded yet.</p> : (
          <ul className="card mt-3 divide-y divide-stone-100 p-0">
            {gradings.map(g => (
              <li key={g.id} className="flex flex-wrap justify-between gap-2 px-5 py-3 text-[15px]">
                <span>{gradeLabel(g.for_rank)}{g.event && <> · <Link to={memberLinks.event(g.event.slug)} className="link">{g.event.title}</Link></>}</span>
                <span className="text-ink-500">{date(g.graded_on)} · {g.result !== 'registered' ? RESULT[g.result] : g.recommended === null ? "Awaiting sensei's approval" : g.recommended ? `Approved by ${g.recommended_by ?? 'sensei'}` : 'Not approved this time'}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h2 className="text-2xl">Bookings</h2>
        {!bookings.length ? <p className="card mt-3 text-[15px] text-ink-500">No bookings.</p> : (
          <ul className="card mt-3 divide-y divide-stone-100 p-0">
            {bookings.map(b => (
              <li key={b.id} className="flex flex-wrap justify-between gap-2 px-5 py-3 text-[15px]">
                <span><Link to={memberLinks.event(b.event!.slug)} className="link">{b.event!.title}</Link>{b.option && <> · {b.option.name}</>}</span>
                <span className="text-ink-500">{b.checked_in_at && <span className="pill mr-2 bg-emerald-100 text-emerald-800">Attended</span>}{dateTime(b.event!.starts_at)} · {b.status === 'pending' ? (b.pay_on_day ? unpaidText(b, b.event?.starts_at) : 'Payment pending') : b.price_pence ? money(b.price_pence) : 'Free'}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {own && (
        <section>
          <h2 className="text-2xl">Payments</h2>
          {payments.isLoading ? <Spinner /> : !payments.data?.length ? <p className="card mt-3 text-[15px] text-ink-500">No payments yet.</p> : (
            <ul className="card mt-3 divide-y divide-stone-100 p-0">
              {payments.data.map(r => (
                <li key={r.id} className="flex flex-wrap justify-between gap-2 px-5 py-3 text-[15px]">
                  <span>{r.kind === 'payment' ? '' : r.kind === 'refund' ? 'Refund: ' : 'Chargeback: '}{r.what_for === 'membership' ? 'Membership' : r.what_for_detail ?? 'Payment'}</span>
                  <span className="text-ink-500">{date(r.on_date)} · {ledgerHow(r)} · <span className={r.amount_pence < 0 ? 'text-brand-700' : 'text-ink-900'}>{r.amount_pence < 0 ? '−' : ''}{money(Math.abs(r.amount_pence))}</span>{['failed', 'cancelled'].includes(r.status) && ` · ${r.status === 'failed' ? 'Failed' : 'Cancelled'}`}</span>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-2 text-sm text-ink-500">Payments that covered several people in the household are listed for each of them.</p>
        </section>
      )}
    </div>
  );
}
